// src/modules/callSync/pages/CallingDashboard.jsx
// Calling productivity (STEP 14/15). Kept deliberately plain: accurate numbers
// first, charts later.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Typography, Segmented, DatePicker, Space,
  Button, Tag, Progress, Alert, Tooltip, message,
} from 'antd';
import {
  PhoneOutlined, ReloadOutlined, CheckCircleOutlined, ClockCircleOutlined,
  InfoCircleOutlined, TrophyOutlined, WarningOutlined,
} from '@ant-design/icons';
import { fetchCallSummary, fetchCallsByRep } from '../api';
import { formatTalkTime, formatDuration, creditModeMeta } from '../constants';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const RANGES = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'Custom', value: 'custom' },
];

/** One target line: "31 / 100" over a bar. Used for all three targets. */
function TargetBar({ label, done, target, percent }) {
  const pct = Math.min(100, percent || 0);
  return (
    <div>
      <Text style={{ fontSize: 11 }}>
        <Text type="secondary" style={{ fontSize: 11 }}>{label} </Text>
        {done} / {target}
      </Text>
      <Progress
        percent={pct}
        size="small"
        status={percent >= 100 ? 'success' : percent >= 60 ? 'active' : 'exception'}
        format={() => `${percent || 0}%`}
      />
    </div>
  );
}

export default function CallingDashboard() {
  const [range, setRange] = useState('today');
  const [custom, setCustom] = useState(null);
  const [summary, setSummary] = useState(null);
  const [reps, setReps] = useState([]);
  const [days, setDays] = useState(1);
  const [loading, setLoading] = useState(false);

  const params = useCallback(() => {
    if (range === 'custom') {
      if (!custom?.[0] || !custom?.[1]) return null;
      return {
        startDate: custom[0].format('YYYY-MM-DD'),
        endDate: custom[1].format('YYYY-MM-DD'),
      };
    }
    return { range };
  }, [range, custom]);

  const load = useCallback(async () => {
    const p = params();
    if (!p) return; // custom range not chosen yet
    setLoading(true);
    try {
      const [s, r] = await Promise.all([fetchCallSummary(p), fetchCallsByRep(p)]);
      setSummary(s.data.data);
      setReps(r.data.data || []);
      setDays(r.data.days || 1);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Could not load calling statistics');
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      title: 'Sales Rep',
      dataIndex: ['salesRep', 'name'],
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v || 'Unknown'}</Text>
          {r.salesRep?.employeeCode && <Text type="secondary" style={{ fontSize: 12 }}>{r.salesRep.employeeCode}</Text>}
        </Space>
      ),
    },
    {
      title: (
        <Tooltip title="Calls the phone actually placed. Pressing Call in the CRM is not counted until the phone dials.">
          Attempted <InfoCircleOutlined />
        </Tooltip>
      ),
      dataIndex: 'callsAttempted',
      sorter: (a, b) => a.callsAttempted - b.callsAttempted,
      defaultSortOrder: 'descend',
    },
    { title: 'Connected', dataIndex: 'callsConnected', render: (v) => <Tag color="green">{v}</Tag> },
    { title: 'No Answer', dataIndex: 'noAnswer' },
    { title: 'Failed', dataIndex: 'failed' },
    {
      title: 'Talk Time',
      dataIndex: 'talkTimeSeconds',
      render: (v, r) => (
        <Space size={4}>
          {formatTalkTime(v)}
          {r.talkTimeIsPartial && (
            <Tooltip title={`${r.assumedConnected} call(s) could not be confirmed from the phone's call log and are excluded from talk time.`}>
              <InfoCircleOutlined style={{ color: '#fa8c16' }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    { title: 'Avg Talk', dataIndex: 'avgTalkTimeSeconds', render: (v) => formatDuration(v) },
    { title: 'Unique Leads', dataIndex: 'uniqueLeadsCalled' },
    { title: 'Follow-ups', dataIndex: 'followUpsCreated' },
    {
      // The gap between Attempted and Counted is the whole point of the rule, so
      // it gets its own column rather than being buried in the progress bar.
      title: (
        <Tooltip title="Calls that actually earned target credit under this rep's rule.">
          Counted <InfoCircleOutlined />
        </Tooltip>
      ),
      dataIndex: 'creditedCalls',
      sorter: (a, b) => a.creditedCalls - b.creditedCalls,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          {r.creditMode !== 'DIALLED_ONLY' && r.callsAttempted > v && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              of {r.callsAttempted} dialled
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: (
        <Tooltip title="Dialled today with no outcome recorded yet — still saveable, still worth credit. 'Lapsed' calls can no longer earn anything.">
          Awaiting result <InfoCircleOutlined />
        </Tooltip>
      ),
      dataIndex: 'awaitingResult',
      render: (v, r) => (
        <Space size={4}>
          {v > 0 ? <Tag color="orange">{v}</Tag> : <Text type="secondary">—</Text>}
          {r.lapsedResult > 0 && (
            <Tooltip title={`${r.lapsedResult} call(s) went unsaved past their business day. That credit is gone.`}>
              <Tag color="red">{r.lapsedResult} lapsed</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: `Target (${days} day${days > 1 ? 's' : ''})`,
      dataIndex: 'targetAchievedPercent',
      render: (v, r) => (
        <Space direction="vertical" size={2} style={{ minWidth: 150 }}>
          <TargetBar
            label="Calls"
            done={r.creditedCalls}
            target={r.periodTarget}
            percent={v}
          />
          {r.periodConnectedTarget > 0 && (
            <TargetBar
              label="Connected"
              done={r.callsConnected}
              target={r.periodConnectedTarget}
              percent={r.connectedAchievedPercent}
            />
          )}
          {r.periodTalkTimeSecondsTarget > 0 && (
            <TargetBar
              label="Talk"
              done={formatTalkTime(r.talkTimeSeconds)}
              target={formatTalkTime(r.periodTalkTimeSecondsTarget)}
              percent={r.talkTimeAchievedPercent}
            />
          )}
          <Tooltip title={creditModeMeta(r.creditMode).help}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {creditModeMeta(r.creditMode).short}
              {r.targetScope && r.targetScope !== 'DEFAULT' ? ` · ${r.targetScope}` : ''}
            </Text>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col><Title level={4} style={{ margin: 0 }}><PhoneOutlined /> Calling Dashboard</Title></Col>
        <Col>
          <Space wrap>
            <Segmented options={RANGES} value={range} onChange={setRange} />
            {range === 'custom' && <RangePicker value={custom} onChange={setCustom} />}
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      {summary?.talkTimeIsPartial && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Some talk time could not be measured"
          description={`${summary.assumedConnected} call(s) were connected according to the phone's call state, but the app could not read the device call log to confirm the exact duration. Those seconds are excluded from talk time rather than estimated.`}
        />
      )}

      {summary?.lapsedResult > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 12 }}
          message="Some calls lost their target credit"
          description={`${summary.lapsedResult} call(s) in this period were dialled but their outcome was never recorded before the business day closed. Under a result-saved rule that credit cannot be recovered.`}
        />
      )}

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic title="Calls Attempted" value={summary?.callsAttempted || 0} prefix={<PhoneOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title={(
                <Tooltip title="Calls that earned daily-target credit. Under a result-saved rule a dialled call with no recorded outcome counts for nothing.">
                  Counted <InfoCircleOutlined />
                </Tooltip>
              )}
              value={summary?.creditedCalls || 0}
              valueStyle={{ color: '#1677ff' }}
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title={(
                <Tooltip title="Dialled, no outcome recorded, business day still open — save these and they count.">
                  Awaiting Result <InfoCircleOutlined />
                </Tooltip>
              )}
              value={summary?.awaitingResult || 0}
              valueStyle={{ color: (summary?.awaitingResult || 0) > 0 ? '#fa8c16' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic title="Connected" value={summary?.callsConnected || 0}
              valueStyle={{ color: '#3f8600' }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small"><Statistic title="No Answer" value={summary?.noAnswer || 0} valueStyle={{ color: '#fa8c16' }} /></Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small"><Statistic title="Failed" value={summary?.failed || 0} valueStyle={{ color: '#cf1322' }} /></Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic title="Total Talk Time" value={formatTalkTime(summary?.talkTimeSeconds || 0)} prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic title="Avg Talk Time" value={formatDuration(summary?.avgTalkTimeSeconds || 0)} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small"><Statistic title="Unique Leads" value={summary?.uniqueLeadsCalled || 0} /></Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small"><Statistic title="Follow-ups Created" value={summary?.followUpsCreated || 0} /></Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic title="Connect Rate" value={summary?.connectRate || 0} suffix="%" />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title={(
                <Tooltip title="Calls dialled directly on the phone, detected from its call log. Kept separate from CRM-tracked calls.">
                  Manual Calls <InfoCircleOutlined />
                </Tooltip>
              )}
              value={summary?.untracked?.count || 0}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" title="By Sales Representative">
        <Table
          rowKey={(r) => String(r.salesRepId)}
          size="small"
          loading={loading}
          columns={columns}
          dataSource={reps}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1500 }}
          locale={{ emptyText: 'No calls in this period' }}
        />
      </Card>
    </div>
  );
}
